import { useEffect } from "react";

import { Switch } from "antd";
import { useDashboardScreenshotStore } from "stores/dashboardScreenshot/useDashboardScreenshotStore";

import { useScreenshotContext } from "../hooks";
import { getStoredDashboardScreenshotSettingsKey } from "../utils";
import { SettingRow } from "./SettingRow";

const WIDGET_BORDER_TOGGLE_ID = "screenshot-setting-widget-border";

/**
 * Standalone toggle that writes directly to the Zustand store,
 * bypassing the screenshot settings form → context → Redux pipeline
 * so it doesn't trigger chart re-renders.
 *
 * The value is persisted in localStorage under the same dashboard
 * screenshot settings key so it survives across sessions.
 *
 * **Why raw localStorage instead of `useLocalStorage`:**
 * `useLocalStorage` is `useState`-backed and unreliable when multiple
 * component instances share the same key (the hook's own limitation,
 * documented in `hooks/useLocalStorage.tsx`). Using it here would also
 * introduce a React state update on toggle that re-renders through the
 * settings tree — the exact re-render we're trying to prevent. Instead
 * we read-merge-write directly so localStorage stays in sync while the
 * Zustand store remains the sole source of truth for the UI.
 */
export function WidgetBorderToggle() {
  const widgetBorderVisible = useDashboardScreenshotStore((s) => s.widgetBorderVisible);
  const setWidgetBorderVisible = useDashboardScreenshotStore(
    (s) => s.setWidgetBorderVisible
  );
  const { widget } = useScreenshotContext();
  const storageKey = getStoredDashboardScreenshotSettingsKey(widget?.widgetId);

  // Restore persisted value on mount
  useEffect(() => {
    if (!storageKey) return;
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) ?? "{}");
      if (typeof stored?.widgetBorderVisible === "boolean") {
        setWidgetBorderVisible(stored.widgetBorderVisible);
      }
    } catch {
      // ignore malformed storage
    }
  }, [storageKey, setWidgetBorderVisible]);

  const handleChange = (checked: boolean) => {
    setWidgetBorderVisible(checked);
    if (!storageKey) return;
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) ?? "{}");
      localStorage.setItem(
        storageKey,
        JSON.stringify({ ...stored, widgetBorderVisible: checked })
      );
    } catch {
      // ignore storage errors
    }
  };

  return (
    <SettingRow label="Widget Border" htmlFor={WIDGET_BORDER_TOGGLE_ID}>
      <Switch
        id={WIDGET_BORDER_TOGGLE_ID}
        checked={widgetBorderVisible}
        onChange={handleChange}
        size="small"
      />
    </SettingRow>
  );
}
